"use client";

import { useState, useEffect } from "react";
import type { DealWithRelations } from "@/lib/deals/types";
import type { DealSignatures } from "@/lib/deals/signature";
import { Upload } from "lucide-react";
import { CompletionCelebration } from "@/components/deals/completion/CompletionCelebration";
import { TransactionSummary } from "@/components/deals/completion/TransactionSummary";
import { DocumentDownloads } from "@/components/deals/completion/DocumentDownloads";
import { routes } from "@/lib/routes";
import { getVisibleDocuments } from "@/lib/deals/types";
import { dealDocumentUrl } from "@/lib/deals/document-download/refs";
interface CompletionStepProps {
  deal: DealWithRelations;
  role: "buyer" | "seller";
  className?: string;
}

/**
 * CompletionStep - Main completion view combining celebration, summary, and documents
 */
export function CompletionStep({
  deal,
  role,
  className = ""
}: CompletionStepProps) {
  const [signatures, setSignatures] = useState<DealSignatures | null>(null);
  const [, setLoading] = useState(true);
  useEffect(() => {
    const fetchSignatures = async () => {
      try {
        const response = await fetch(`/api/deals/${deal.id}/signature`);
        if (response.ok) {
          const data = await response.json();
          setSignatures(data.data);
        }
      } catch (error) {
        console.error("Failed to fetch signatures:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSignatures();
  }, [deal.id]);

  // Collect this role's own uploaded documents, each with an authorized
  // download href (finding H3). Filtering the role-visible set by uploader
  // preserves the prior "own documents only" behavior while keeping the
  // deal-doc index in sync with what the seam resolves.
  const documents = getVisibleDocuments(deal, role).filter(d => d.uploaded_by === role);
  const signedContractHref = signatures?.signed_contract_url ? dealDocumentUrl(deal.id, "signed-contract", {
    download: true
  }) : null;
  return <div className={`space-y-6 ${className}`}>
      {/* Celebration banner */}
      <CompletionCelebration dealId={deal.id} />

      <div className="grid md:grid-cols-2 gap-6">
        {/* Transaction summary */}
        <TransactionSummary deal={deal} role={role} />

        {/* Document downloads */}
        <DocumentDownloads signedContractHref={signedContractHref} documents={documents} />
      </div>

      {/* Next steps / Support info */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-5">
        <h3 className="font-semibold text-blue-900 mb-2">Was passiert als Nächstes?</h3>
        <div className="space-y-3 text-sm text-blue-800">
          {role === "buyer" && <>
              <p>
                Die Ökopunkte wurden auf Ihr Konto übertragen. Sie können diese nun für Ihre
                Ausgleichsmaßnahmen verwenden.
              </p>
              <p>
                Bei Fragen zur Nutzung oder zur Dokumentation wenden Sie sich bitte an unser
                Support-Team.
              </p>
            </>}
          {role === "seller" && <>
              <p>
                Die Auszahlung wurde veranlasst und sollte innerhalb von 3-5 Werktagen auf Ihrem
                Konto eingehen.
              </p>
              <p>
                Sie erhalten eine Zahlungsbestätigung per E-Mail, sobald die Überweisung erfolgt
                ist.
              </p>
            </>}
        </div>
      </div>

      {/* Seller: prompt to upload updated Ökokonto document */}
      {role === "seller" && <div className="rounded-lg border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Upload className="w-4.5 h-4.5 text-amber-700" />
            </div>
            <div>
              <h3 className="font-semibold text-amber-900 mb-1">
                Aktuellen Ökokonto-Auszug hochladen
              </h3>
              <p className="text-sm text-amber-800">
                Bitte laden Sie nach Abbuchung der Ökopunkte einen aktuellen Ökokonto-Auszug hoch.
                So können wir Ihr Angebot auf dem neuesten Stand halten.
              </p>
              <a href={routes.offerings.edit(deal.offering_id ?? "")} className="inline-flex items-center gap-2 mt-3 px-4 py-2 text-sm font-medium text-amber-800 bg-amber-100 rounded-lg hover:bg-amber-200 transition-colors">
                <Upload className="w-4 h-4" />
                Angebot bearbeiten
              </a>
            </div>
          </div>
        </div>}

      {/* Support contact */}
      <div className="text-center py-4">
        <p className="text-sm text-zinc-500">
          Haben Sie Fragen?{" "}
          <a href="mailto:support@oekopunktemarkt.de" className="text-primary hover:text-primary/80 font-medium">
            Kontaktieren Sie uns
          </a>
        </p>
      </div>
    </div>;
}